import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { redirect, useActionData, useNavigation, useSubmit } from 'react-router-dom';
import moment from 'moment';
import { isEmpty, pluck } from 'ramda';
import Cookies from 'js-cookie';
import useValidator from '../../hooks/useValidator';
import { showAlert, subscribeToExternals, useAppContext } from '../../context/appContext';
import {
  getCookie,
  isEnvironment,
  isRole,
  setCookie,
  storage,
  AUTH_TOKEN_STORE_KEY,
  AUTH_USER_STORE_KEY,
  USER_COMING_FROM_STORE_KEY,
  isNativeApp,
  sessionTStorage,
} from '../../utils/utils';
import Alert from '../../components/Alert/Alert';
import PhoneInput from '../../components/Inputs/PhoneInput/PhoneInput';
import PinInput from '../../components/Inputs/PinInput/PinInput';
import Button from '../../components/Button/Button';
import useCheckInternet from '../../hooks/useCheckInternet';
import { getAuthUser, login, syncNotificationToken } from '../../api/api';
import { paths } from '../../routes';
import { Role } from '../../types/types';

const customValidators = {
  phone: {
    message: 'The phone number must be a valid US number.',
    rule: (val: string) => {
      return isValidPhoneNumber(val);
    },
    required: false,
  },
};

export const authQuery = () => ({
  queryKey: ['auth'],
  queryFn: () => getAuthUser(),
});

export function loader() {
  return async () => {
    if (getCookie(AUTH_TOKEN_STORE_KEY)) {
      return redirect(paths.HOME_URL_PATH);
    }
    return null;
  };
}

export function action() {
  return async ({ request }: { request: Request }) => {
    const values = Object.fromEntries(await request.formData()) as {
      phone: string;
      pin: string;
      from?: string;
      notificationToken?: string;
    };

    let errors = {};

    try {
      const response = await login(values.phone, values.pin);

      if (response === 'OTP-SENT') return { hasSentOTP: true };

      await setCookie(
        AUTH_TOKEN_STORE_KEY,
        response.token,
        Math.ceil(moment.duration(moment(response.expiresAt).diff(moment())).asDays()),
      );

      let user = await getAuthUser();

      user = {
        ...user,
        roles: user?.roles ? (pluck('name' as any, user.roles) satisfies Role[]) : [],
      };

      if (
        (!isRole(user.roles, 'CARE-HEALTH-GUIDE') &&
          !isRole(user.roles, 'COLLABORATING-PHYSICIAN')) ||
        ['CARE-HEALTH-GUIDE', 'COLLABORATING-PHYSICIAN'].every(fndRole =>
          user.roles.includes(fndRole),
        )
      ) {
        throw new Error(
          `Oops. Looks like you're not authorized to access this application. Please contact the support team!`,
        );
      }

      if (values.notificationToken && !isEmpty(values.notificationToken)) {
        await syncNotificationToken({
          app: 'GUIDE',
          oneSignalToken: values.notificationToken,
        });
      }

      storage.set(AUTH_USER_STORE_KEY, JSON.stringify(user));

      subscribeToExternals(user);
    } catch (error: any) {
      Cookies.remove(AUTH_TOKEN_STORE_KEY); // remove cookie when any error
      errors = { message: error?.message };
    }

    if (Object.keys(errors).length) {
      return { errors };
    }

    if (values.from) {
      sessionTStorage.remove(USER_COMING_FROM_STORE_KEY);
    }

    return redirect(values.from || paths.HOME_URL_PATH);
  };
}

function Login() {
  const [phone, setPhone] = React.useState('');
  const [pin, setPin] = React.useState('');
  const [notificationToken, setNotificationToken] = React.useState('');
  const [from] = React.useState(() => sessionTStorage.get(USER_COMING_FROM_STORE_KEY));
  const [{ alert }, dispatch] = useAppContext();
  const [validator, showValidationMessage] = useValidator({}, customValidators);
  const hasInternet = useCheckInternet();
  const navigation = useNavigation();
  const submit = useSubmit();

  const { hasSentOTP, errors }: any = useActionData() || {};

  React.useEffect(() => {
    (async function getOneSignalInfoTokenId() {
      if (isNativeApp()) {
        const getSignalInfo = await window?.gonative?.onesignal?.onesignalInfo();
        setNotificationToken(getSignalInfo?.oneSignalUserId);
      }
    })();
  }, []);

  React.useEffect(() => {
    showAlert(dispatch, {
      show: !hasInternet,
      message: `Sorry! Looks like you are currently Offline. Could not connect to River!`,
      type: 'ERROR',
    });
  }, [dispatch, hasInternet]);

  React.useEffect(() => {
    if (isEnvironment('development') || isEnvironment('staging')) {
      showAlert(
        dispatch,
        {
          show: true,
          message: `You are currently using staging (TESTING) system!`,
          type: 'ERROR',
        },
        5000,
      );
    }
  }, [dispatch]);

  React.useEffect(() => {
    if (errors && Object.keys(errors).length) {
      setPin('');
      showAlert(
        dispatch,
        {
          show: true,
          message: errors.message,
          type: 'ERROR',
        },
        6000,
      );
    }
  }, [dispatch, errors]);

  const handleLogin = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    const formData = new FormData();
    formData.append('phone', phone);
    formData.append('pin', pin);
    if (from) {
      formData.append('from', from);
    }

    if (notificationToken) {
      formData.append('notificationToken', notificationToken);
    }

    submit(formData, { method: 'post' });
  };

  return (
    <div
      className="flex flex-col h-auto overflow-y-scroll tablet:grid tablet:grid-cols-[40%_60%] tablet:h-screen 
      text-river-black tablet:overflow-hidden"
    >
      <Alert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onAction={() => showAlert(dispatch)}
        onActionMessage="Close"
      />

      <div
        className="bg-login-onboarding-mobile-graphic h-[250px] bg-[#FFE0AF] bg-cover bg-center 
      bg-no-repeat tablet:bg-login-onboarding-graphic tablet:h-full"
      />

      <div className="tablet:relative text-center p-8 tablet:h-auto">
        <div
          className={`${
            alert.show ? 'tablet:pt-10' : ''
          } flex flex-col items-center text-center self-center h-full w-full tablet:justify-center`}
        >
          <h1 className="font-semibold text-xl tablet:text-2xl">Welcome Back</h1>
          <div className="w-fit mt-6 mb-9">
            <p className="text-lg w-full laptop:w-[450px]">
              Never, ever be afraid to make some noise and get in good trouble, necessary trouble.{' '}
              <span className="font-semibold inline-flex">- John Lewis</span>
            </p>

            {!hasSentOTP ? (
              <div className="mt-12 tablet:mt-16 w-full mx-auto laptop:w-[367px]">
                <PhoneInput
                  name="phone"
                  placeholder="(223) 498-3974"
                  value={phone}
                  onChange={value => setPhone(value)}
                  className="peer-[.validator-error]/phone:!border-river-red 
                  peer-[.validator-error]/phone:!mb-5 peer-[.validator-error]/phone:focus:!ring-river-red"
                  validator={validator.message('phone', phone, 'required|phone', {
                    messages: {
                      required: 'Phone number is required.',
                    },
                    className: 'peer/phone validator-error text-center mt-3 top-[40px] ',
                  })}
                  onKeyPress={async event => {
                    const keyPressed = event.key || event.keyCode || event.which;
                    if (keyPressed === 13 || keyPressed === 'Enter') {
                      handleLogin();
                    }
                  }}
                />
              </div>
            ) : (
              <div className="mt-12 tablet:mt-16">
                <PinInput
                  name="pin"
                  length={4}
                  initialValue={pin}
                  focus
                  secret={false}
                  type="numeric"
                  onChange={value => setPin(value)}
                  className="peer-[.validator-error]/pin:!border-river-red 
                    peer-[.validator-error]/pin:!mb-5 peer-[.validator-error]/pin:focus:!ring-river-red"
                  validator={validator.message('pin', pin, 'required|numeric|min:4', {
                    messages: {
                      required: 'Enter the 4 digit code.',
                      min: 'Enter the correct 4 digit code sent.',
                    },
                    className: 'peer/pin validator-error mt-3 top-[40px]',
                  })}
                />
              </div>
            )}
          </div>
          <p className="w-[350px] text-sm">
            {!hasSentOTP ? (
              'To sign in, enter your phone number to receive a verification code.'
            ) : (
              <>
                A 4 digit code was sent to{' '}
                <span className="inline-flex">{`***-***-${phone ? phone.slice(-4) : 8976}`}</span>,
                enter the code in the box above
              </>
            )}
          </p>
          <Button
            label={!hasSentOTP ? 'Continue' : 'Verify'}
            disabled={navigation.state === 'submitting' || navigation.state === 'loading'}
            loading={navigation.state === 'submitting' || navigation.state === 'loading'}
            onClick={() => handleLogin()}
            className="mt-[20%] w-[250px]"
          />
        </div>
      </div>
    </div>
  );
}

export default Login;
